import { unref } from 'vue';
import { useMutation, UseMutationConfig } from '@libs/use';
import { CreateOrderResponse } from '@libs/api-reference';
import { Fn } from '@vueuse/core';

import * as API from '../requests';
import { ORDER_SIDES } from '../constants';

export type TCreateOfflineOrderFormModel = {
  quantity: number;
  price: number | null;
  totalValue: number;
  orderExecutionType: string;
};

type TOfflineOrderFactoryOptions = {
  formModel: TCreateOfflineOrderFormModel;
  symbol: string;
  type: 'buy' | 'sell';
};

type TUseCreateOfflineOrderOptions = {
  symbol: string;
  type: TOfflineOrderFactoryOptions['type'];
  onReject?: UseMutationConfig<CreateOrderResponse>['onReject'];
  onSuccess?: UseMutationConfig<CreateOrderResponse>['onSuccess'];
};

function offlineOrderFactory({
  formModel,
  symbol,
  type,
}: TOfflineOrderFactoryOptions) {
  const today = new Date();

  return {
    orderExecutionType: formModel.orderExecutionType,
    instrumentIdentification: symbol,
    orderSide: ORDER_SIDES[type],
    clientTag: 'Falcon-Offline',
    validityDate: new Date(today.setDate(today.getDate() + 7)),
    quantity: formModel.quantity,
    price: formModel.price,
    value: formModel.price === null ? formModel.totalValue : 0,
  };
}

export default function useCreateOfflineOrderQuery({
  symbol,
  type,
  onReject,
  onSuccess,
}: TUseCreateOfflineOrderOptions) {
  const {
    execute: createOfflineOrder,
    isPending: isLoadingCreateOfflineOrder,
    ...query
  } = useMutation<CreateOrderResponse>(
    ({ formModel }: { formModel: TCreateOfflineOrderFormModel }) =>
      API.createOfflineOrder(
        offlineOrderFactory({
          formModel,
          symbol: unref(symbol),
          type,
        })
      ),
    {
      onSuccess,
      onReject,
    }
  );

  function createMarketOrder(
    args: {
      formModel: TCreateOfflineOrderFormModel;
      resetFormModel?: Fn;
      isMarketOrderBasedOnQuantity: boolean;
    } & Record<string, any>
  ) {
    args.formModel.price = null;

    if (args.isMarketOrderBasedOnQuantity) {
      args.formModel.totalValue = 0;
    } else {
      args.formModel.quantity = 0;
      args.formModel.totalValue = Math.floor(args.formModel.totalValue);
    }

    createOfflineOrder(args);
  }

  return {
    createOfflineOrder,
    isLoadingCreateOfflineOrder,
    createMarketOrder,
    ...query,
  };
}
